import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'
import { CompactButton } from 'components/sto/Section'
import { TabCardBody } from 'components/sto/Tabs'
import { stoColor, stoMedia, stoRadius, stoShadow, stoSpace, stoType } from 'constants/stoTheme'

/** Dividends keeps its heading close to the rows on mobile (Figma 5207:30431). */
export const Body = styled(TabCardBody)`
  ${stoMedia.belowDesktop} {
    gap: ${stoSpace[2]};
  }
`

/** Heading and button side by side; on mobile the button moves under the content. */
export const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${stoSpace[4]} ${stoSpace[6]};

  ${stoMedia.belowDesktop} {
    display: contents;
  }
`

/** Title (with its tooltip) over the subtitle; the button wraps under it once they no longer fit. */
export const Heading = styled.div`
  display: flex;
  flex: 1 1 240px;
  flex-direction: column;
  min-width: 0;

  ${stoMedia.belowDesktop} {
    /* Now a child of the column Body: the row's flex basis would become a height. */
    flex: none;
  }
`

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${stoSpace[2]};
`

/** Figma 3329:69266: body/md SemiBold with body/lg tracking. A <p>: the global h3 rule would resize it. */
export const Title = styled.p`
  /* Only as wide as its text, so the info icon follows it. */
  flex: 0 1 auto;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.6px;
  text-transform: none;
  color: ${stoColor.textPrimary};
`

/** Figma 3329:69267: body/md Regular, text/tertiary. Back-office rich text: may hold links. */
export const Subtitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.6px;
  color: ${stoColor.neutral500};

  p {
    margin: 0;
  }

  a {
    color: ${stoColor.textSecondary} !important;
    text-decoration: underline;
  }
`

/** View full history. Mobile: full width under the rows, at the regular outlined size (Figma 5207:30648). */
export const DeclarationAction = styled(CompactButton)`
  ${stoMedia.belowDesktop} {
    order: 1;
    justify-content: center;
    width: 100%;
    margin-top: ${stoSpace[4]};
    padding: 10px ${stoSpace[6]};
  }
`

/**
 * The rows, in one bordered card (Figma 5193:21366).
 *
 * Selectors here use elements and data attributes, never another component's class: the server
 * and the browser name styled components differently, and a row that re-renders after hydration
 * swaps to the browser's names while its untouched neighbours keep the server's.
 */
export const Rows = styled.div`
  overflow: hidden;
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  box-shadow: ${stoShadow.xs};

  & > * + * {
    border-top: 1px solid ${stoColor.borderSubtle};
  }
`

export const Stats = styled.dl`
  display: grid;
  /* At least as wide as the longest word, so back-office text never breaks mid-word. */
  grid-template-columns: repeat(3, minmax(min-content, 1fr));
  margin: 0;

  ${stoMedia.belowDesktop} {
    grid-template-columns: repeat(2, minmax(min-content, 1fr));
    gap: ${stoSpace[6]} ${stoSpace[3]};
  }
`

export const Stat = styled.div`
  min-width: 0;
  padding: ${stoSpace[4]};

  ${stoMedia.desktopUp} {
    &[data-only='mobile'] {
      display: none;
    }
  }

  ${stoMedia.belowDesktop} {
    padding: 0;

    &[data-only='desktop'] {
      display: none;
    }
  }
`

export const StatLabel = styled.dt`
  ${stoType.statLabel};
  color: ${stoColor.textSecondary};
`

export const StatValue = styled.dd`
  ${stoType.metricValue};
  margin: 0;
  text-transform: uppercase;
  overflow-wrap: break-word;
  color: ${stoColor.textPrimary};
`

const DURATION = '300ms'
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'

/** One distribution: its first line, and the details under it (`data-open`). */
export const Row = styled.div`
  position: relative;
  padding-right: ${stoSpace[12]};
  cursor: pointer;

  ${stoMedia.belowDesktop} {
    padding: ${stoSpace[3]} 44px ${stoSpace[3]} ${stoSpace[3]};
  }
`

/** Animates height without measuring: grid rows transition between 0fr and 1fr, as Collapsible. */
export const Details = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition:
    grid-template-rows ${DURATION} ${EASING},
    opacity ${DURATION} ${EASING};

  [data-open='true'] > & {
    grid-template-rows: 1fr;
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const DetailsInner = styled.div`
  min-height: 0;
  overflow: hidden;

  /* The gap under the first line. On the child: padding here would stay when collapsed. */
  ${stoMedia.belowDesktop} {
    & > dl {
      padding-top: ${stoSpace[6]};
    }
  }
`

/** Opens and closes the row, centred on its first line. */
export const Toggle = styled.button`
  position: absolute;
  top: 22px;
  right: ${stoSpace[4]};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: ${stoRadius.full};
  background: transparent;
  color: ${stoColor.textPrimary};
  cursor: pointer;

  svg {
    transition: transform ${DURATION} ${EASING};
  }

  [data-open='true'] > & svg {
    transform: rotate(180deg);
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }

  ${stoMedia.belowDesktop} {
    top: 14px;
    right: 6px;
  }
`

/** "View declaration" in an open row: outlined on desktop, a centred text button on mobile. */
export const RowAction = styled.div`
  padding: 0 ${stoSpace[4]} ${stoSpace[4]};

  ${stoMedia.belowDesktop} {
    display: flex;
    justify-content: center;
    padding: ${stoSpace[3]} 0 0;
    /* Back under the toggle's column: the button is centred on the whole row. */
    margin-right: -32px;

    /* a[href]: as specific as the button's own link colour rule, which it must beat. */
    a[href] {
      border-color: transparent;
      color: ${stoColor.textSecondary} !important;
    }
  }
`

export type NextDividendPlacement = 'aside' | 'hero'

/**
 * Figma 3410:90004 / 3582:127071. One copy per breakpoint, hidden by CSS so the server
 * render is the same for both: above KeyActions on desktop, under the hero metrics on mobile.
 */
export const NextBox = styled(Link)<{ $placement: NextDividendPlacement }>`
  display: flex;
  flex-direction: column;
  padding: ${stoSpace[3]} ${stoSpace[6]};
  border-radius: ${stoRadius.lg};
  background: ${stoColor.infoSurface};
  color: ${stoColor.textPrimary} !important;
  text-decoration: none;

  /* A card, not a text link: no fade from the global link hover. */
  &&:hover {
    opacity: 1;
  }

  /* Mobile: a 1px border in its own colour, as the metric tiles' border, so the text lines up with theirs. */
  ${stoMedia.belowDesktop} {
    padding: ${stoSpace[3]};
    border: 1px solid ${stoColor.infoSurface};
  }

  ${({ $placement }) =>
    $placement === 'aside'
      ? css`
          ${stoMedia.belowDesktop} {
            display: none;
          }
        `
      : css`
          ${stoMedia.desktopUp} {
            display: none;
          }

          /* In the hero's identity block, after the metrics (order 2). */
          ${stoMedia.belowDesktop} {
            order: 3;
            margin-top: ${stoSpace[4]};
          }
        `}
`

/** Figma 974:2441: the frequency, e.g. Daily. */
export const NextLine = styled.span`
  ${stoType.stripeValue};
  color: ${stoColor.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/** Figma 974:2441: "DIVIDEND FREQUENCY". */
export const NextTitle = styled.span`
  ${stoType.stripeLabel};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${stoColor.textSecondary};

  /* The size of the metric labels above it on mobile. */
  ${stoMedia.belowDesktop} {
    font-size: 11px;
  }
`
